/**
 * Informe de anuncios por propiedad (para el propietario): normalizador de `getunitreport` /
 * `getsharedunitreport` y ayudas de los links con vencimiento. Puro.
 * Contrato: argenlotapi `docs/frontend/FRONTEND_INFORME_PROPIEDAD.md`.
 *
 * Reglas de producto (Ignacio, 27/10): el informe muestra **todo**, siempre con gasto y costos (no hay
 * casilla para ocultarlos; el editor también lo ve); consultas = cantidades reales sin datos personales;
 * nombres de anuncios y campañas tal cual vienen de Meta; el gasto de un anuncio compartido se reparte en
 * partes iguales (`equal_split`) y se explica siempre que se aplicó.
 */
import { metaNum, stripMetaTerrioMark } from './metaFormat';
import { readMetaRemainingScope } from './metaBudget';
import { metaAgeLabel, metaAgeRank, metaCountryLabel, metaGenderLabel, metaPlacementLabel, metaPlatformLabel, metaRegionLabel } from './metaLabels';

const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const bool = (v) => v === true;
const list = (v) => (Array.isArray(v) ? v : []);
const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : null);
const httpsUrl = (v) => {
  const s = str(v);
  return s && /^https:\/\//i.test(s) ? s : null;
};
const hexColor = (v) => {
  const s = str(v);
  return s && /^#[0-9a-f]{6}$/i.test(s) ? s : null;
};

const coordNum = (v, min, max) => {
  const n = typeof v === 'number' ? v : (typeof v === 'string' && v.trim() ? Number(v) : NaN);
  return Number.isFinite(n) && n >= min && n <= max ? n : null;
};

/**
 * `unit.location` (28/10) → `{ lat, lng, coordSource, boundary, boundarySource, bbox, address }` o `null`.
 * Sin punto válido → `null` (el informe se muestra solo, sin mapa). `boundary` = anillo `[{lat, lng}]`
 * (≥ 3 puntos válidos, si no `null`: solo marcador); `bbox` = `[minLng, minLat, maxLng, maxLat]` o `null`.
 */
export function readUnitLocation(raw) {
  const loc = obj(raw);
  if (!loc) return null;
  const lat = coordNum(loc.lat, -90, 90);
  const lng = coordNum(loc.lng, -180, 180);
  if (lat == null || lng == null) return null;
  const ring = list(loc.boundary)
    .map((c) => ({ lat: coordNum(c?.lat, -90, 90), lng: coordNum(c?.lng, -180, 180) }))
    .filter((c) => c.lat != null && c.lng != null);
  const boundary = ring.length >= 3 ? ring : null;
  const box = list(loc.bbox).map((v, i) => coordNum(v, -(i % 2 ? 90 : 180), i % 2 ? 90 : 180));
  const bbox = box.length === 4 && box.every((v) => v != null) && box[0] <= box[2] && box[1] <= box[3] ? box : null;
  const addr = obj(loc.address);
  const address = addr ? {
    street: str(addr.street),
    neighborhood: str(addr.neighborhood),
    locality: str(addr.locality),
    municipality: str(addr.municipality),
    province: str(addr.province),
    label: str(addr.label),
  } : null;
  return {
    lat,
    lng,
    coordSource: str(loc.coord_source),
    boundary,
    boundarySource: boundary ? str(loc.boundary_source) : null,
    bbox,
    address: address && Object.values(address).some(Boolean) ? address : null,
  };
}

/** Motivos por los que el alcance real puede no estar (`reach.unavailable_reason`). */
const REACH_REASONS = {
  NO_ADS: 'Todavía no hay anuncios para esta propiedad.',
  MULTIPLE_ACCOUNTS: 'Los anuncios de esta propiedad están en más de una cuenta publicitaria: Meta no permite contar las personas sin repetir.',
  TOO_MANY_ADS: 'Hay demasiados anuncios en esta propiedad para calcular el alcance real.',
  META_NOT_CONNECTED: 'Meta no está conectado.',
  META_TOKEN_EXPIRED: 'Se cortó la conexión con Meta: reconectala para ver el alcance real.',
  META_TOKEN_CRYPTO: 'Se cortó la conexión con Meta: reconectala para ver el alcance real.',
  META_RATE_LIMITED: 'Meta nos pidió esperar un momento. El alcance real vuelve a calcularse en unos minutos.',
  META_UNAVAILABLE: 'Meta no respondió. El alcance real vuelve a calcularse más tarde.',
  META_TIME_BUDGET: 'Meta tardó demasiado en responder. El alcance real vuelve a calcularse más tarde.',
  META_API_ERROR: 'Meta respondió con un error al calcular el alcance real.',
};

/** Texto del alcance cuando no hay número (nunca un error). */
export function metaReachUnavailableText(reason) {
  const code = str(reason)?.toUpperCase();
  return (code && REACH_REASONS[code]) || 'Alcance no disponible';
}

const readBreakdown = (rows, labeler, unknownLabel = 'Desconocido') => list(rows).map((r) => {
  const isUnknown = r?.is_unknown === true;
  // Meta puede mandar la fila desconocida con key vacía: se conserva con key `unknown`.
  const key = String(r?.key ?? '').trim() || (isUnknown ? 'unknown' : '');
  return {
    key,
    // `is_unknown` (27/10): la API ya manda `label` «Desconocida» / «Desconocido»; si faltara, se pone acá.
    label: str(r?.label) || (isUnknown ? unknownLabel : labeler(key, r)),
    isUnknown,
    contacts: metaNum(r?.contacts),
    impressions: metaNum(r?.impressions),
    clicks: metaNum(r?.clicks),
    linkClicks: metaNum(r?.link_clicks),
    shareOfContactsPct: metaNum(r?.share_of_contacts_pct),
    shareOfImpressionsPct: metaNum(r?.share_of_impressions_pct),
  };
}).filter((r) => r.key);

const placementLabel = (key) => {
  const [platform, position] = String(key).split('/');
  return position ? metaPlacementLabel(platform, position) : metaPlatformLabel(platform);
};

function readAudience(raw) {
  const a = obj(raw);
  if (!a || a.available === false) return null;
  const cov = obj(a.geo_data_coverage) || obj(a.data_coverage);
  const dataCov = obj(a.data_coverage);
  return {
    label: str(a.label) || 'Público de la campaña',
    approximate: bool(a.approximate),
    approximateReason: str(a.approximate_reason),
    limited: bool(a.limited_by_special_category),
    limitedNote: str(a.limited_note),
    age: readBreakdown(a.age, (k) => metaAgeLabel(k)).sort((x, y) => metaAgeRank(x.key) - metaAgeRank(y.key)),
    gender: readBreakdown(a.gender, (k) => metaGenderLabel(k)),
    platform: readBreakdown(a.platform, (k) => metaPlatformLabel(k)),
    position: readBreakdown(a.position, (k) => placementLabel(k)),
    region: readBreakdown(a.region, (k) => metaRegionLabel(k), 'Desconocida'),
    country: readBreakdown(a.country, (k) => metaCountryLabel(k), 'Desconocido'),
    // Contactos por región (27/10): con `false` las regiones van por impresiones y con la nota de la API.
    regionContactsComplete: a.region_contacts_complete !== false,
    regionContactsNote: str(a.region_contacts_note),
    // Cobertura: si no cubre el período se muestra la nota de la API (geo: `geo_data_coverage ?? data_coverage`).
    coverageNotes: [dataCov && dataCov.complete === false ? str(dataCov.note) : null,
      cov && cov !== dataCov && cov.complete === false ? str(cov.note) : null].filter(Boolean),
  };
}

function readInteractions(raw) {
  const i = obj(raw);
  if (!i || i.available === false) return null;
  const v = obj(i.video) || {};
  return {
    total: metaNum(i.total_interactions),
    reactions: metaNum(i.reactions),
    comments: metaNum(i.comments),
    shares: metaNum(i.shares),
    saves: metaNum(i.saves),
    pageLikes: metaNum(i.page_likes),
    linkClicks: metaNum(i.link_clicks),
    clicks: metaNum(i.clicks),
    outboundClicks: metaNum(i.outbound_clicks),
    landingPageViews: metaNum(i.landing_page_views),
    costPerEngagement: metaNum(i.cost_per_engagement),
    video: {
      plays: metaNum(v.video_plays),
      views3s: metaNum(v.video_views_3s),
      thruplays: metaNum(v.video_thruplays),
      hookRatePct: metaNum(v.video_hook_rate_pct),
      thruplayRatePct: metaNum(v.video_thruplay_rate_pct),
      avgWatchS: metaNum(v.video_avg_watch_time_s),
    },
  };
}

function readSpend(raw) {
  const s = obj(raw);
  // `included: false` (solo si algún día se pide `include_spend=0`): sin ninguna cifra de plata.
  if (!s || s.included === false) return null;
  return {
    allocation: str(s.allocation),
    allocationApplied: bool(s.allocation_applied),
    allocationNote: str(s.allocation_note),
    currency: str(s.currency),
    mixedCurrencies: bool(s.mixed_currencies),
    lifetimeComplete: s.lifetime_complete !== false,
    inPeriod: metaNum(s.in_period),
    lifetime: metaNum(s.lifetime),
    costPerContact: metaNum(s.cost_per_contact),
    costPerLinkClick: metaNum(s.cost_per_link_click),
    costPerEngagement: metaNum(s.cost_per_engagement),
    cpm: metaNum(s.cpm),
    byCurrency: list(s.by_currency).map((c) => ({
      currency: str(c?.currency),
      inPeriod: metaNum(c?.in_period),
      lifetime: metaNum(c?.lifetime),
      costPerContact: metaNum(c?.cost_per_contact),
    })).filter((c) => c.currency),
  };
}

function readAd(raw, index) {
  const m = obj(raw?.metrics) || {};
  const sp = obj(raw?.spend);
  return {
    key: `${index}`,
    name: str(stripMetaTerrioMark(raw?.ad_name)) || 'Anuncio',
    campaignName: str(stripMetaTerrioMark(raw?.campaign_name)),
    adsetName: str(stripMetaTerrioMark(raw?.adset_name)),
    creativeKind: str(raw?.creative_kind),
    thumbnailUrl: httpsUrl(raw?.thumbnail_url),
    deliveryLabel: str(raw?.delivery_label),
    isDelivering: bool(raw?.is_delivering),
    isShared: bool(raw?.is_shared),
    sharedUnitsCount: metaNum(raw?.shared_units_count),
    metrics: {
      impressions: metaNum(m.impressions),
      clicks: metaNum(m.clicks),
      linkClicks: metaNum(m.link_clicks),
      contacts: metaNum(m.contacts),
      waConversations: metaNum(m.wa_conversations),
      leads: metaNum(m.leads),
      engagement: metaNum(m.post_engagement),
    },
    spend: sp ? {
      currency: str(sp.currency),
      inPeriod: metaNum(sp.in_period),
      lifetime: metaNum(sp.lifetime),
      adTotalInPeriod: metaNum(sp.ad_total_in_period),
      adTotalLifetime: metaNum(sp.ad_total_lifetime),
      allocation: str(sp.allocation),
      costPerContact: metaNum(sp.cost_per_contact),
    } : null,
  };
}

function readCampaign(raw, index) {
  const b = obj(raw?.budget);
  return {
    key: `${index}`,
    name: str(stripMetaTerrioMark(raw?.campaign_name)) || 'Campaña',
    deliveryLabel: str(raw?.delivery_label),
    isDelivering: bool(raw?.is_delivering),
    adsInReport: metaNum(raw?.ads_in_report),
    campaignAdsCount: metaNum(raw?.campaign_ads_count),
    hasOtherAds: bool(raw?.has_other_ads),
    budget: b ? {
      level: str(b.level),
      currency: str(b.currency),
      daily: metaNum(b.daily),
      lifetime: metaNum(b.lifetime),
      remaining: metaNum(b.remaining),
      remainingScope: readMetaRemainingScope(b, metaNum(b.remaining), metaNum(b.lifetime), metaNum(b.daily)),
      note: str(b.note),
    } : null,
  };
}

function readInquiries(raw) {
  const q = obj(raw);
  if (!q || q.available === false) return null;
  const origin = obj(q.origin_labels) || {};
  const stage = obj(q.stage_labels) || {};
  const entries = (counts, labels) => Object.entries(obj(counts) || {})
    .map(([key, value]) => ({ key, label: str(labels[key]) || key, value: metaNum(value) }))
    .filter((e) => e.value != null);
  return {
    inPeriod: metaNum(q.in_period),
    total: metaNum(q.total),
    lastDate: str(q.last_inquiry_date),
    interestedPeople: metaNum(q.interested_people),
    byOrigin: entries(q.by_origin_in_period, origin),
    byStage: entries(q.interested_by_stage, stage),
  };
}

function readHistory(raw) {
  const h = obj(raw);
  if (!h || h.available === false) return null;
  return {
    availableSince: str(h.available_since),
    note: str(h.note),
    changes: list(h.changes).map((c, i) => {
      const price = obj(c?.price);
      const status = obj(c?.status);
      const operation = obj(c?.operation);
      return {
        key: `${i}`,
        date: str(c?.date),
        inPeriod: bool(c?.in_period),
        price: price ? { from: metaNum(price.from), to: metaNum(price.to), currency: str(price.currency) } : null,
        status: status ? { from: str(status.from_label) || str(status.from), to: str(status.to_label) || str(status.to) } : null,
        operation: operation ? { from: str(operation.from_label) || str(operation.from), to: str(operation.to_label) || str(operation.to) } : null,
      };
    }),
  };
}

/**
 * `data` de `getunitreport` / `getsharedunitreport` (o el envelope entero) → modelo del informe.
 * Todo bloque puede faltar: viene `null` / vacío y la vista lo omite. Sin datos → `null`.
 *
 * @param {object} res Envelope `{ data }` o el `data` directo.
 */
export function readUnitReport(res) {
  const data = obj(res?.data) || (obj(res) && (res.summary || res.unit) ? res : null);
  if (!data) return null;
  const summary = obj(data.summary) || {};
  const reach = obj(data.reach) || {};
  const reachValue = metaNum(reach.value ?? summary.reach);
  const unit = obj(data.unit) || {};
  const brand = obj(data.agent_branding);
  const period = obj(data.period);
  return {
    status: data.report_status === 'no_ads' ? 'no_ads' : 'ok',
    generatedAt: str(data.generated_at),
    period: period ? { since: str(period.since), until: str(period.until), days: metaNum(period.days) } : null,
    freshness: { through: str(data.data_through), stale: bool(data.data_stale), text: str(data.data_freshness_text) },
    branding: brand ? {
      name: str(brand.brand_name),
      logoUrl: httpsUrl(brand.logo_url),
      color: hexColor(brand.primary_color),
      phone: str(brand.phone),
      whatsapp: str(brand.whatsapp),
      email: str(brand.email),
    } : null,
    unit: {
      kind: str(unit.item_kind),
      label: str(unit.label),
      projectLabel: str(unit.project_label),
      price: metaNum(unit.price),
      currency: str(unit.currency),
      operationLabel: str(unit.operation_label),
      statusLabel: str(unit.status_label),
      location: readUnitLocation(unit.location),
    },
    summary: {
      contacts: metaNum(summary.contacts),
      waConversations: metaNum(summary.wa_conversations),
      leads: metaNum(summary.leads),
      impressions: metaNum(summary.impressions),
      reach: reachValue,
      frequency: metaNum(summary.frequency),
      clicks: metaNum(summary.clicks),
      linkClicks: metaNum(summary.link_clicks),
      landingPageViews: metaNum(summary.landing_page_views),
      adsCount: metaNum(summary.ads_count),
      activeAdsCount: metaNum(summary.active_ads_count),
      campaignsCount: metaNum(summary.campaigns_count),
    },
    reach: {
      value: reachValue,
      stale: bool(reach.stale),
      // Con número no hace falta el motivo; sin número, el texto de `unavailable_reason`.
      unavailableText: reachValue == null ? metaReachUnavailableText(reach.unavailable_reason) : null,
      unavailableReason: str(reach.unavailable_reason),
    },
    series: list(data.series).map((b, i) => ({
      key: `${i}`,
      start: str(b?.bucket_start),
      end: str(b?.bucket_end),
      days: metaNum(b?.days),
      contacts: metaNum(b?.contacts),
      impressions: metaNum(b?.impressions),
      linkClicks: metaNum(b?.link_clicks),
    })),
    interactions: readInteractions(data.interactions),
    audience: readAudience(data.audience),
    campaigns: list(data.campaigns).map(readCampaign),
    ads: list(data.ads).map(readAd),
    spend: readSpend(data.spend),
    inquiries: readInquiries(data.inquiries),
    history: readHistory(data.history),
    notes: list(data.notes).map(str).filter(Boolean),
    isPublicLink: Boolean(obj(data.link)?.is_public_link),
    linkExpiresAt: str(obj(data.link)?.expires_at),
  };
}

// ── Links con vencimiento ────────────────────────────────────────────────────

/**
 * URL pública del informe: `<origen>/#/informe/<token>`. La app usa HashRouter, así que el token
 * queda en el fragmento (nunca viaja al servidor del Front ni en el `Referer`).
 */
export function buildUnitReportShareUrl(token, loc = typeof window !== 'undefined' ? window.location : null) {
  const t = str(token);
  if (!t || !loc?.origin) return '';
  return `${loc.origin}${loc.pathname || '/'}#/informe/${encodeURIComponent(t)}`;
}

const SHARE_STATUS = {
  active: 'Activo',
  expired: 'Vencido',
  revoked: 'Revocado',
};

/** Un link de `getunitreportshares` / `createunitreportshare` (el `token` solo viene al crearlo). */
export function readUnitReportShare(raw) {
  const period = obj(raw?.period);
  const status = SHARE_STATUS[raw?.status] ? raw.status : 'expired';
  return {
    id: str(raw?.share_id),
    status,
    statusLabel: SHARE_STATUS[status],
    period: period ? { since: str(period.since), until: str(period.until) } : null,
    createdAt: str(raw?.created_at),
    expiresAt: str(raw?.expires_at),
    revokedAt: str(raw?.revoked_at),
    lastViewedAt: str(raw?.last_viewed_at),
    viewCount: metaNum(raw?.view_count) ?? 0,
    token: str(raw?.token),
  };
}

export function readUnitReportShares(res) {
  const rows = list(res?.data?.shares).map(readUnitReportShare).filter((s) => s.id);
  return rows;
}

export const countActiveShares = (shares) => list(shares).filter((s) => s.status === 'active').length;

/** Opciones de vencimiento al crear un link (máximo 90). */
export const UNIT_REPORT_EXPIRY_OPTIONS = [7, 15, 30, 60, 90].map((days) => ({ days, label: `${days} días` }));
